import React, { useState } from "react";
import { User, Sparkles, ArrowRight } from "lucide-react";
import { Modal } from "../common/Modal";
import { loginAnonymously } from "../../lib/firebase";
import { useAuthStore } from "../../stores/authStore";
import type { UserProfile } from "../../types";

interface AnonymousGuestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NICKNAME_PRESETS = [
  "Rockstar Sam",
  "Karaoke King",
  "Vocal Queen",
  "Disco Ninja",
  "Soul Siren",
  "Melody Maker",
  "Echo Champ",
];

export const AnonymousGuestModal: React.FC<AnonymousGuestModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { setUser } = useAuthStore();
  const [nickname, setNickname] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRandomize = () => {
    const random = NICKNAME_PRESETS[Math.floor(Math.random() * NICKNAME_PRESETS.length)];
    setNickname(random);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanNick = nickname.trim() || "Guest Singer";
    setLoading(true);
    setError(null);

    try {
      await loginAnonymously(cleanNick);
      onClose();
    } catch (err: any) {
      console.warn("Firebase Anonymous Auth not enabled, creating local guest session:", err);
      // Seamless guest session fallback
      const guestUid = `anon_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
      const guestProfile: UserProfile = {
        uid: guestUid,
        displayName: cleanNick,
        firstName: cleanNick.split(" ")[0],
        photoURL: `https://api.dicebear.com/7.x/bottts/svg?seed=${encodeURIComponent(cleanNick)}`,
        email: null,
        isAnonymous: true,
        currentPartyId: null,
      };
      setUser(guestProfile);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Join as Guest Singer">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-xs text-text-muted">
          Enter a nickname to quickly join parties and add songs to the shared karaoke queue.
        </p>

        {error && (
          <div className="p-3 text-xs bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl">
            {error}
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-text-muted">Your Nickname</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-text-muted">
              <User className="w-4 h-4" />
            </div>
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="e.g. Karaoke Hero"
              maxLength={25}
              required
              className="w-full pl-10 pr-10 py-3 bg-background border border-border focus:border-primary rounded-2xl text-text-main placeholder-text-muted text-sm outline-none transition-colors"
            />
            <button
              type="button"
              onClick={handleRandomize}
              className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-accent hover:text-accent-hover transition-colors cursor-pointer"
              title="Randomize Nickname"
            >
              <Sparkles className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="pt-2 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 px-4 rounded-2xl bg-card border border-border hover:border-text-muted text-text-muted text-xs font-semibold transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-3 px-4 rounded-2xl bg-primary hover:bg-primary-hover text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition-all shadow-lg shadow-primary/20 cursor-pointer disabled:opacity-50"
          >
            {loading ? "Joining..." : "Continue"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </form>
    </Modal>
  );
};
